import React, { FC, useEffect, useState, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';
import Button from '../../components/ui/Button'; // Import Button component
import { CompanyDetails, CompanyVerificationStatus, JobPostResponse } from '../../types/api';
import { CompanyProfileData } from '../../types/components';
import companyService from '../../services/company.service';
import jobService from '../../services/job.service'; // Assuming a job service exists to fetch job posts

interface MinimalJobPostCardProps {
  jobPost: JobPostResponse;
  companyProfileData: CompanyProfileData | null;
}

const MinimalJobPostCard: FC<MinimalJobPostCardProps> = ({ jobPost, companyProfileData }) => {
  const formatSalary = (minSalary?: number, maxSalary?: number, currency?: string): string => {
    if (minSalary && maxSalary) {
      return `${currency || ''} ${minSalary.toLocaleString()} - ${currency || ''} ${maxSalary.toLocaleString()}`;
    }
    if (minSalary) return `${currency || ''} ${minSalary.toLocaleString()}`;
    if (maxSalary) return `${currency || ''} ${maxSalary.toLocaleString()}`;
    return 'Salary not specified';
  };

  const location = companyProfileData ? `${companyProfileData.city || 'N/A'}, ${companyProfileData.state || 'N/A'}` : 'N/A';
  const { pathname } = useLocation();
  const userRole = pathname.split('/')[1];

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-2">
      <h4 className="text-lg font-semibold text-gray-900">{jobPost.jobTitle || 'N/A'}</h4>
      <div className="flex flex-wrap gap-2 text-sm text-gray-600">
        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">{jobPost.jobType || 'N/A'}</span>
        { companyProfileData?.city && (<span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">{location}</span>)}
        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">Openings: {jobPost.noOfOpenings || 0}</span>
      </div>
      <p className="text-gray-700 line-clamp-2">{jobPost.jobDescription || 'No description provided.'}</p>
      <p className="text-md font-bold text-primary-lightGreen">{formatSalary(jobPost.minSalary, jobPost.maxSalary, jobPost.salaryCurrency)}</p>
      <Link to={`/${userRole}/post/${jobPost.postId}`}>
        <Button variant="outline">View Details</Button>
      </Link>
    </div>
  );
};

const PublicCompanyProfile: FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [companyData, setCompanyData] = useState<CompanyProfileData | null>(null);
  const [jobPosts, setJobPosts] = useState<JobPostResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [jobLoading, setJobLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const location = useLocation();

  const showMoreDetails = location.pathname.toLocaleLowerCase().startsWith('/agent') || location.pathname.toLocaleLowerCase().startsWith('/admin');
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);

  const fetchJobPosts = async () => {
    if (!companyId) {
      setJobError("Company ID is missing for job posts.");
      setJobLoading(false);
      return;
    }
    try {
      setJobLoading(true);
      const response = await jobService.getAllCompanyJobPosts(companyId);
      setJobPosts(response.data.posts || []);
    } catch (err) {
      setJobError("Failed to fetch job posts.");
      console.error("Error fetching job posts:", err);
    } finally {
      setJobLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (!companyId) {
        setError("Company ID is missing.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await companyService.getCompanyDetails(companyId);
        const companyDetails = response.data;

        let otherDetailsParsed: any = {};
        if (companyDetails.otherDetails) {
          try {
            otherDetailsParsed = JSON.parse(companyDetails.otherDetails);
          } catch (parseError) {
            console.error("Error parsing otherDetails:", parseError);
          }
        }
        let adminUserOrFirstUser = companyDetails.users.find(user => user.isAdmin == true) || companyDetails.users[0];

        const mappedCompanyData: CompanyProfileData = {
          companyName: companyDetails.companyName,
          companyId: companyDetails.companyId,
          gstNumber: companyDetails.gstin || '',
          msmeNumber: otherDetailsParsed.msmeNumber || '',
          panNumber: otherDetailsParsed.panNumber || '',
          registrationType: otherDetailsParsed.registrationType || 'gstin',
          registrationNumber: otherDetailsParsed.registrationNumber || '',
          businessType: otherDetailsParsed.businessType || '',
          industry: companyDetails.industry,
          companySize: companyDetails.empStrength,
          foundedYear: otherDetailsParsed.foundedYear || '',
          website: companyDetails.webpageUrl,
          description: companyDetails.description,
          address: companyDetails.address,
          city: otherDetailsParsed.officeCity || '',
          state: otherDetailsParsed.officeState || '',
          pincode: otherDetailsParsed.officePincode || '',
          country: otherDetailsParsed.officeCountry || '',
          contactPerson: otherDetailsParsed.contactPerson || adminUserOrFirstUser ? (adminUserOrFirstUser.firstName || "") + (adminUserOrFirstUser.lastName || "") : '',
          contactEmail: otherDetailsParsed.contactEmail || adminUserOrFirstUser ? adminUserOrFirstUser.email || ""  : '',
          contactPhone: otherDetailsParsed.contactPhone || adminUserOrFirstUser ? adminUserOrFirstUser.mobileNumber || "" : '',
          hrPerson: otherDetailsParsed.contactPerson || '',
          hrEmail: otherDetailsParsed.contactEmail || '',
          hrPhone: otherDetailsParsed.contactPhone || '',
          linkedinUrl: otherDetailsParsed.linkedinUrl || '',
          twitterUrl: otherDetailsParsed.twitterUrl || '',
          facebookUrl: otherDetailsParsed.facebookUrl || '',
          instagramUrl: otherDetailsParsed.instagramUrl || '',
          attachments: companyDetails.attachments || [],
          isVerified: companyDetails.verificationStatus === 'Verified',
          verificationStatus: companyDetails.verificationStatus,
          registrationStep: otherDetailsParsed.registrationStep || 0,
          shouldSubmitProfile: otherDetailsParsed.shouldSubmitProfile || false,
        };
        setCompanyData(mappedCompanyData);
      } catch (err) {
        setError("Failed to fetch company details.");
        console.error("Error fetching company details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, [companyId]);

  useEffect(() => {
    if (companyData) {
      fetchJobPosts();
    }
  }, [companyData]);

  const getVerificationStatusColor = (status: CompanyVerificationStatus): string => {
    switch (status) {
      case 'Verified': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Blocked': return 'bg-gray-100 text-gray-800';
      case 'Incomplete': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationStatusText = (status: CompanyVerificationStatus): string => {
    switch (status) {
      case 'Verified': return 'Verified';
      case 'Pending': return 'Pending Review';
      case 'Rejected': return 'Rejected';
      case 'Blocked': return 'Blocked';
      case 'Incomplete': return 'Incomplete';
      default: return 'Not Submitted';
    }
  };

  const getAttachmentLink = (typeId: string) => {
    const attachment = companyData?.attachments?.find(att => att.typeId === typeId);
    if (attachment && attachment.fileUrl) {
      return (
        <a href={attachment.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary-lightGreen hover:underline">
          {attachment.originalName || typeId}
        </a>
      );
    }
    return "N/A";
  };

  const renderMoreDetails = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">More Details</h3>
        <Button
          variant='ghost'
          onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
          // className="lg:hidden" // Only show button on smaller screens
        >
          {showAdditionalDetails ? 'Hide' : 'Show'}
        </Button>
      </div>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          showAdditionalDetails ? 'max-h-screen' : 'max-h-0'
        }`}
      >
        <div className="grid grid-cols-1 sm:grid-col-2 lg:grid-col-1 gap-4 pt-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Company ID</p>
            <p className="text-sm text-gray-900">{companyData?.companyId || "N/A"}</p>
          </div>
          <div className='flex'>
            <p className="text-sm font-medium text-gray-500 pr-1">Verification Status</p>
            <Badge className={getVerificationStatusColor(companyData?.verificationStatus as CompanyVerificationStatus|| 'Incomplete')}>
              {getVerificationStatusText(companyData?.verificationStatus as CompanyVerificationStatus|| 'Incomplete')}
            </Badge>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">GST Number</p>
            <p className="text-sm text-gray-900">{companyData?.gstNumber || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">GST Certificate</p>
            <p className="text-sm text-gray-900">{getAttachmentLink('CompanyDocumentGST')}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">PAN Number</p>
            <p className="text-sm text-gray-900">{companyData?.panNumber || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">PAN Card</p>
            <p className="text-sm text-gray-900">{getAttachmentLink('CompanyDocumentPAN')}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">MSME Number</p>
            <p className="text-sm text-gray-900">{companyData?.msmeNumber || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">MSME Certificate</p>
            <p className="text-sm text-gray-900">{getAttachmentLink('CompanyDocumentMSME')}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Registration Type</p>
            <p className="text-sm text-gray-900">{companyData?.registrationType || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">CIN / Registration Number</p>
            <p className="text-sm text-gray-900">{companyData?.registrationNumber || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">CIN Certificate</p>
            <p className="text-sm text-gray-900">{getAttachmentLink('CompanyDocumentCIN')}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Business Type</p>
            <p className="text-sm text-gray-900">{companyData?.businessType || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Founded Year</p>
            <p className="text-sm text-gray-900">{companyData?.foundedYear || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Pincode</p>
            <p className="text-sm text-gray-900">{companyData?.pincode || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Country</p>
            <p className="text-sm text-gray-900">{companyData?.country || "N/A"}</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">Error: {error}</div>
    );
  }

  if (!companyData) {
    return (
      <div className="text-center py-12 text-gray-600">No company data found.</div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {/* {companyData.companyName} */}
              Company Profile
            </h1>
            {/* <p className="text-gray-600">Public Profile</p> */}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Company Info & Contact */}
        <div className="lg:col-span-1 space-y-6">
          {/* Company Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-4 overflow-hidden">
                {companyData.attachments && companyData.attachments.find(att => att.typeId === 'CompanyLogo')?.fileUrl ? (
                  <img
                    src={companyData.attachments.find(att => att.typeId === 'CompanyLogo')?.fileUrl}
                    alt="Company Logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {companyData.companyName}
              </h2>
              <p className="text-gray-600 mb-2">
                {companyData.industry || "N/A"} •{" "}
                {companyData.companySize || "N/A"} employees
              </p>
              {/* Public profile should not show verification status */}
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
               
                {companyData.website ? (
                  <div className="flex items-center">
                  <a
                    href={companyData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-gray-900 hover:text-primary-lightGreen"
                  >
                    <svg
                      className="w-5 h-5 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                    <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  </svg>
                    <span> {companyData.website}</span>
                  </a>
                  </div>
                ) : (
                  <span className="text-sm text-gray-600">N/A</span>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  {companyData.city || companyData.state ?
                  <>
                    {companyData.city || "N/A"},{" "}
                    {companyData.state || "N/A"}
                  </> : <> {companyData.address || "N/A"} </>
                  } 
                </span>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Social Media
              </h3>
              {!companyData.linkedinUrl &&
                !companyData.twitterUrl &&
                !companyData.facebookUrl &&
                !companyData.instagramUrl && (
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                      <span>LinkedIn : N/A</span>
                    </div>
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                      </svg>
                      <span>Twitter : N/A</span>
                    </div>
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      <span>Facebook : N/A</span>
                    </div>
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z" />
                      </svg>
                      <span>Instagram : N/A</span>
                    </div>
                  </div>
                )}
              <div className="space-y-2">
                {companyData.linkedinUrl && (
                  <div className="flex items-center">
                  <a
                    href={companyData.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                  </div>
                )}
                {companyData.twitterUrl && (
                   <div className="flex items-center">
                  <a
                    href={companyData.twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    <span>Twitter</span>
                  </a>
                  </div>
                )}
                {companyData.facebookUrl && (
                  <div className="flex items-center">
                  <a
                    href={companyData.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span>Facebook</span>
                  </a>
                  </div>
                )}
                {companyData.instagramUrl && (
                  <div className="flex items-center">
                  <a
                    href={companyData.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-lightGreen"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z" />
                    </svg>
                    <span>Instagram</span>
                  </a>
                  </div>
                )}
              </div>
            </div>
          </div>
          {showMoreDetails && renderMoreDetails()}
        </div>

        {/* Right Column - Detailed Information */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Company Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              About Us
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {companyData.description}
            </p>

            {/* <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">
                Mission Statement
              </h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                N/A
              </p>
            </div>

            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">
                Company Values
              </h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" size="sm">N/A</Badge>
              </div>
            </div> */}
          </div>

          {/* Contact Information */}
          {showMoreDetails && 
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-2">
                  Primary Contact
                </h4>
                <div className="space-y-1">
                  <p className="text-sm text-gray-900">
                    {companyData.contactPerson || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {companyData.contactEmail || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {companyData.contactPhone || "N/A"}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-2">
                  HR Contact
                </h4>
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">
                    {companyData.hrPerson || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {companyData.hrEmail || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {companyData.hrPhone || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-2">
                Office Address
              </h4>
              <div className="text-sm text-gray-900">
                <p>{companyData.address}</p>
              </div>
            </div>
          </div>
          }
          {/* Job Posts */}
          {showMoreDetails && <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Open Job Posts
              </h3>
              <Button variant="ghost" onClick={fetchJobPosts} disabled={jobLoading}>
                {jobLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
            {jobLoading ? (
              <div className="flex justify-center items-center h-32">
                <Loader size="md" />
              </div>
            ) : jobError ? (
              <div className="text-center py-4 text-red-600">
                Error: {jobError}
              </div>
            ) : jobPosts.length === 0 ? (
              <div className="text-center py-4 text-gray-600">
                No active job posts found.
              </div>
            ) : (
              <div className="space-y-4">
                {jobPosts.map((jobPost) => (
                  <MinimalJobPostCard key={jobPost.postId} jobPost={jobPost} companyProfileData={companyData} />
                ))}
              </div>
            )}
          </div>
          }
        </div>
      </div>
    </div>
  );
};

export default PublicCompanyProfile;
